import React, { useState, useEffect, useRef } from "react";
import { RefreshCw, Download } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend
} from "recharts";

import "./finance.css";

// ─── PDF Generator ─────────────────────────────────────────────────────────────
const generateBudgetPDF = (currentList, filter, getMonthName, COLORS) => {
  const printWindow = window.open("", "_blank");

  const grandTotal = currentList.reduce((sum, b) => sum + Number(b.total_amount), 0);
  const totalItems = currentList.reduce((sum, b) => sum + (b.items ? b.items.length : 0), 0);

  const getPeriodLabel = (budget) => {
    if (filter === "monthly") return `${getMonthName(budget.month)} ${budget.year}`;
    if (filter === "seasonal") return `${budget.crop_name} — ${budget.season} ${budget.year}`;
    return `Combined #${budget.id}`;
  };

  const fmt = (val) =>
    Number(val || 0).toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  // Summary rows
  const summaryRows = currentList
    .map((budget, index) => {
      const itemCount = budget.items ? budget.items.length : 0;
      const avgPrice = itemCount > 0 ? (Number(budget.total_amount) / itemCount).toFixed(2) : 0;
      const color = COLORS[index % COLORS.length];
      return `
        <tr>
          <td>
            <span style="background:${color}20;color:${color};padding:5px 11px;border-radius:7px;font-weight:700;font-size:13px;">
              ${getPeriodLabel(budget)}
            </span>
          </td>
          <td style="text-align:right;">Rs ${fmt(budget.total_amount)}</td>
          <td style="text-align:center;">${itemCount}</td>
          <td style="text-align:right;">Rs ${fmt(avgPrice)}</td>
        </tr>`;
    })
    .join("");

  // Detailed item blocks
  const detailBlocks =
    filter !== "combined"
      ? currentList
          .map((budget, budgetIndex) => {
            const color = COLORS[budgetIndex % COLORS.length];
            const itemRows = (budget.items || [])
              .map((item, idx) => {
                const pct =
                  Number(budget.total_amount) > 0
                    ? ((Number(item.price) / Number(budget.total_amount)) * 100).toFixed(1)
                    : 0;
                const avg = Number(budget.total_amount) / (budget.items.length || 1);
                const status = Number(item.price) > avg ? "Above Avg" : "Below Avg";
                const statusColor = Number(item.price) > avg ? "#ff4e4e" : "#00a859";
                return `
                <tr>
                  <td style="text-align:center;color:#6b8070;">${idx + 1}</td>
                  <td style="font-weight:500;">${item.name}</td>
                  <td style="text-align:right;color:#0f5323;font-weight:600;">Rs ${fmt(item.price)}</td>
                  <td style="text-align:center;">
                    <div style="display:flex;align-items:center;gap:6px;justify-content:center;">
                      <div style="width:70px;height:7px;background:rgba(15,83,35,0.1);border-radius:4px;overflow:hidden;">
                        <div style="width:${pct}%;height:100%;background:${color};border-radius:4px;"></div>
                      </div>
                      <span style="font-size:12px;font-weight:700;">${pct}%</span>
                    </div>
                  </td>
                  <td style="text-align:center;">
                    <span style="background:${statusColor}20;color:${statusColor};padding:3px 9px;border-radius:6px;font-size:12px;font-weight:700;">${status}</span>
                  </td>
                </tr>`;
              })
              .join("");

            return `
            <div style="margin-bottom:32px;">
              <div style="font-family:'Playfair Display',serif;font-size:17px;color:#0f5323;padding:11px 15px;border-bottom:2px solid #0f5323;background:rgba(15,83,35,0.05);border-radius:8px 8px 0 0;margin-bottom:0;">
                ${getPeriodLabel(budget)} — Budget Items
              </div>
              <table>
                <thead>
                  <tr>
                    <th style="text-align:center;">#</th>
                    <th>Item Name</th>
                    <th style="text-align:right;">Price (Rs)</th>
                    <th style="text-align:center;">% of Total</th>
                    <th style="text-align:center;">Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${itemRows}
                  <tr class="total-row">
                    <td colspan="2"><strong>SUBTOTAL</strong></td>
                    <td style="text-align:right;"><strong>Rs ${fmt(budget.total_amount)}</strong></td>
                    <td style="text-align:center;"><strong>100%</strong></td>
                    <td style="text-align:center;"><strong>${(budget.items || []).length} items</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>`;
          })
          .join("")
      : "";

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${filter.charAt(0).toUpperCase() + filter.slice(1)} Budget Report</title>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Source+Sans+3:wght@400;600;700&display=swap" rel="stylesheet">
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Source Sans 3', sans-serif; background: #f5f7f2; color: #1a2e1e; padding: 40px; }

        .cover {
          background: linear-gradient(135deg, #0f5323 0%, #1a7a35 60%, #2ea84e 100%);
          border-radius: 16px;
          padding: 48px;
          color: white;
          margin-bottom: 32px;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .cover-title { font-family: 'Playfair Display', serif; font-size: 36px; line-height: 1.1; }
        .cover-sub { font-size: 14px; opacity: 0.8; margin-top: 8px; }
        .cover-meta { text-align: right; font-size: 13px; opacity: 0.85; line-height: 1.8; }
        .cover-meta strong { display: block; font-size: 24px; font-family: 'Playfair Display', serif; }

        .kpi-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 28px; }
        .kpi-card {
          background: white; border-radius: 12px; padding: 22px;
          border-left: 5px solid; box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .kpi-label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #6b8070; }
        .kpi-value { font-size: 26px; font-weight: 700; margin-top: 6px; font-family: 'Playfair Display', serif; }

        .section-head {
          font-family: 'Playfair Display', serif; font-size: 18px; color: #0f5323;
          padding: 12px 16px; border-bottom: 2px solid #0f5323;
          background: rgba(15,83,35,0.05); border-radius: 8px 8px 0 0; margin-bottom: 0;
        }
        table { width: 100%; border-collapse: collapse; background: white; border-radius: 0 0 10px 10px; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.06); margin-bottom: 28px; }
        thead tr { background: rgba(15,83,35,0.08); }
        th { padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #0f5323; font-weight: 700; }
        td { padding: 10px 12px; border-top: 1px solid #e8f0ea; font-size: 14px; color: #2c4030; }
        tr:hover td { background: #f0f7f2; }
        .total-row td { background: rgba(15,83,35,0.08); font-weight: 700; font-size: 14px; border-top: 2px solid #0f5323; color: #0f5323; }
        .grand-total td { background: linear-gradient(135deg,#0f5323,#1a7a35); color: white; font-weight: 800; font-size: 15px; padding: 14px 12px; }

        .watermark { text-align: center; margin-top: 48px; font-size: 12px; color: #99b8a0; border-top: 1px solid #d0e4d6; padding-top: 20px; }

        @media print {
          body { background: white; padding: 20px; }
          .cover, .kpi-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table, .kpi-row { break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="cover">
        <div>
          <div class="cover-title">${filter.charAt(0).toUpperCase() + filter.slice(1)} Budget Report</div>
          <div class="cover-sub">Sadaat Agro Farms — Financial Overview</div>
        </div>
        <div class="cover-meta">
          <strong>Rs ${fmt(grandTotal)}</strong>
          Grand Total Budget
          <br>
          Generated: ${new Date().toLocaleDateString("en-PK", { year: "numeric", month: "long", day: "numeric" })}
        </div>
      </div>

      <div class="kpi-row">
        <div class="kpi-card" style="border-color:#387eff;">
          <div class="kpi-label">Budget Periods</div>
          <div class="kpi-value" style="color:#387eff;">${currentList.length}</div>
        </div>
        <div class="kpi-card" style="border-color:#00a859;">
          <div class="kpi-label">Grand Total</div>
          <div class="kpi-value" style="color:#00a859;">Rs ${fmt(grandTotal)}</div>
        </div>
        <div class="kpi-card" style="border-color:#ff9800;">
          <div class="kpi-label">Total Items</div>
          <div class="kpi-value" style="color:#ff9800;">${totalItems}</div>
        </div>
      </div>

      <!-- SUMMARY TABLE -->
      <div class="section-head">Budget Summary</div>
      <table>
        <thead>
          <tr>
            <th>Period</th>
            <th style="text-align:right;">Total Amount (Rs)</th>
            <th style="text-align:center;">Items Count</th>
            <th style="text-align:right;">Avg Item Price</th>
          </tr>
        </thead>
        <tbody>
          ${summaryRows}
          <tr class="grand-total">
            <td><strong>GRAND TOTAL</strong></td>
            <td style="text-align:right;"><strong>Rs ${fmt(grandTotal)}</strong></td>
            <td style="text-align:center;"><strong>${totalItems}</strong></td>
            <td style="text-align:right;"><strong>Rs ${totalItems > 0 ? fmt(grandTotal / totalItems) : "0.00"}</strong></td>
          </tr>
        </tbody>
      </table>

      <!-- DETAILED ITEMS -->
      ${detailBlocks}

      <div class="watermark">
        Generated automatically by Sadaat Agro Farms Management System &nbsp;•&nbsp; ${new Date().toISOString()}
      </div>

      <script>window.onload = () => { window.print(); }<\/script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

// ──────────────────────────────────────────────────────────────────────────────

const Budget = () => {
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("monthly");

  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  // ✅ NEW SEASONAL STATES
  const [selectedCrop, setSelectedCrop] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [selectedSeasonYear, setSelectedSeasonYear] = useState("");

  // ✅ DROPDOWN OPEN STATES
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [cropDropdownOpen, setCropDropdownOpen] = useState(false);
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);
  const [seasonYearDropdownOpen, setSeasonYearDropdownOpen] = useState(false);

  // ✅ CHECK IF ANY DROPDOWN IS OPEN
  const isAnyDropdownOpen = monthDropdownOpen || yearDropdownOpen || cropDropdownOpen || seasonDropdownOpen || seasonYearDropdownOpen;

  // ✅ DROPDOWN REFS
  const monthDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const cropDropdownRef = useRef(null);
  const seasonDropdownRef = useRef(null);
  const seasonYearDropdownRef = useRef(null);

  const [budgetData, setBudgetData] = useState({
    monthly: [],
    seasonal: [],
    combined: []
  });

  const COLORS = ["#387eff", "#ff4e4e", "#00a859", "#d80097", "#ff9800"];

  const getMonthName = (monthNumber) => {
    const months = [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December"
    ];
    return months[Number(monthNumber) - 1] || monthNumber;
  };

  // ✅ CLOSE DROPDOWNS WHEN CLICKING OUTSIDE
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) {
        setMonthDropdownOpen(false);
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setYearDropdownOpen(false);
      }
      if (cropDropdownRef.current && !cropDropdownRef.current.contains(event.target)) {
        setCropDropdownOpen(false);
      }
      if (seasonDropdownRef.current && !seasonDropdownRef.current.contains(event.target)) {
        setSeasonDropdownOpen(false);
      }
      if (seasonYearDropdownRef.current && !seasonYearDropdownRef.current.contains(event.target)) {
        setSeasonYearDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadBudgetDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const res = await fetch(
        " https://sadaatagrofarms.com/api/budget/dashboard/",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      const data = await res.json();

      setBudgetData({
        monthly: data.monthly_budgets || [],
        seasonal: data.seasonal_budgets || [],
        combined: data.combined_budgets || []
      });

      setLoading(false);
    } catch (error) {
      console.error("Budget Dashboard Fetch Error:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBudgetDashboard();
  }, []);

  // ✅ FILTER MONTHLY
  const getFilteredMonthly = () => {
    return budgetData.monthly.filter((budget) => {
      const matchMonth = selectedMonth
        ? Number(budget.month) === Number(selectedMonth)
        : true;

      const matchYear = selectedYear
        ? Number(budget.year) === Number(selectedYear)
        : true;

      return matchMonth && matchYear;
    });
  };

  // ✅ FILTER SEASONAL
  const getFilteredSeasonal = () => {
    return budgetData.seasonal.filter((budget) => {
      const matchCrop = selectedCrop
        ? budget.crop_name === selectedCrop
        : true;

      const matchSeason = selectedSeason
        ? budget.season === selectedSeason
        : true;

      const matchYear = selectedSeasonYear
        ? Number(budget.year) === Number(selectedSeasonYear)
        : true;

      return matchCrop && matchSeason && matchYear;
    });
  };

  // ✅ FILTER FOR CHART
  const getFilteredData = () => {
    if (filter === "monthly") {
      return getFilteredMonthly().flatMap((budget) =>
        budget.items.map((item) => ({
          name: `${item.name} (${getMonthName(budget.month)} ${budget.year})`,
          value: Number(item.price)
        }))
      );
    }

    if (filter === "seasonal") {
      return getFilteredSeasonal().flatMap((budget) =>
        budget.items.map((item) => ({
          name: `${item.name} (${budget.crop_name} - ${budget.season} ${budget.year})`,
          value: Number(item.price)
        }))
      );
    }

    if (filter === "combined") {
      return budgetData.combined.flatMap((combined) => {
        const monthly = budgetData.monthly.find(
          (m) => m.id === combined.monthly_budget
        );
        const seasonal = budgetData.seasonal.find(
          (s) => s.id === combined.seasonal_budget
        );

        const data = [];

        if (monthly) {
          data.push({
            name: `Monthly (${getMonthName(monthly.month)} ${monthly.year})`,
            value: Number(monthly.total_amount)
          });
        }

        if (seasonal) {
          data.push({
            name: `Seasonal (${seasonal.crop_name})`,
            value: Number(seasonal.total_amount)
          });
        }

        return data;
      });
    }

    return [];
  };

  const chartData = getFilteredData();

  const getCurrentList = () => {
    if (filter === "monthly") return getFilteredMonthly();
    if (filter === "seasonal") return getFilteredSeasonal();
    if (filter === "combined") return budgetData.combined;
    return [];
  };

  const currentList = getCurrentList();

  return (
    <div className="finance">
      {/* ✅ BACKDROP BLUR OVERLAY */}
      {isAnyDropdownOpen && <div className="budget-dropdown-backdrop" />}
      
      <div className="page-header">
        <div>
          <h1>Budget Dashboard</h1>
          <p className="subtitle">
            Monthly, Seasonal & Combined Budget Overview
          </p>
        </div>

        {/* ── Header buttons: PDF Download + Refresh ── */}
        <div style={{ display: "flex", gap: 10 }}>
          {currentList.length > 0 && (
            <button
              className="refresh-btn"
              style={{ background: "linear-gradient(135deg,#0f5323,#2ea84e)", color: "white", border: "none" }}
              onClick={() => generateBudgetPDF(currentList, filter, getMonthName, COLORS)}
            >
              <Download size={16} />
              Download PDF
            </button>
          )}
          <button className="refresh-btn" onClick={loadBudgetDashboard}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: "20px" }}>
        <button className="refresh-btn" onClick={() => setFilter("monthly")}>
          Monthly
        </button>
        <button className="refresh-btn" onClick={() => setFilter("seasonal")}>
          Seasonal
        </button>
        <button className="refresh-btn" onClick={() => setFilter("combined")}>
          Combined
        </button>
      </div>

      {/* ✅ MONTHLY CUSTOM DROPDOWNS */}
      {filter === "monthly" && (
        <div style={{ marginBottom: "20px", display: "flex", gap: "30px" }}>
          {/* Month Dropdown */}
          <div className="budget-custom-dropdown" ref={monthDropdownRef}>
            <div 
              className="budget-selected"
              onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}
            >
              {selectedMonth ? getMonthName(selectedMonth) : "All Months"}
            </div>

            {monthDropdownOpen && (
              <div className="budget-options">
                <div
                  className="budget-option placeholder"
                  onClick={() => {
                    setSelectedMonth("");
                    setMonthDropdownOpen(false);
                  }}
                >
                  All Months
                </div>

                {[...new Set(budgetData.monthly.map((b) => b.month))].map((month) => (
                  <div
                    key={month}
                    className="budget-option"
                    onClick={() => {
                      setSelectedMonth(month);
                      setMonthDropdownOpen(false);
                    }}
                  >
                    {getMonthName(month)}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Year Dropdown */}
          <div className="budget-custom-dropdown" ref={yearDropdownRef}>
            <div 
              className="budget-selected"
              onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
            >
              {selectedYear || "All Years"}
            </div>

            {yearDropdownOpen && (
              <div className="budget-options">
                <div
                  className="budget-option placeholder"
                  onClick={() => {
                    setSelectedYear("");
                    setYearDropdownOpen(false);
                  }}
                >
                  All Years
                </div>

                {[...new Set(budgetData.monthly.map((b) => b.year))].map((year) => (
                  <div
                    key={year}
                    className="budget-option"
                    onClick={() => {
                      setSelectedYear(year);
                      setYearDropdownOpen(false);
                    }}
                  >
                    {year}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ✅ SEASONAL CUSTOM DROPDOWNS */}
      {filter === "seasonal" && (
        <div style={{ marginBottom: "20px", display: "flex", gap: "30px" }}>
          {/* Crop Dropdown */}
          <div className="budget-custom-dropdown" ref={cropDropdownRef}>
            <div 
              className="budget-selected"
              onClick={() => setCropDropdownOpen(!cropDropdownOpen)}
            >
              {selectedCrop || "All Crops"}
            </div>

            {cropDropdownOpen && (
              <div className="budget-options">
                <div
                  className="budget-option placeholder"
                  onClick={() => {
                    setSelectedCrop("");
                    setCropDropdownOpen(false);
                  }}
                >
                  All Crops
                </div>

                {[...new Set(budgetData.seasonal.map((b) => b.crop_name))].map((crop) => (
                  <div
                    key={crop}
                    className="budget-option"
                    onClick={() => {
                      setSelectedCrop(crop);
                      setCropDropdownOpen(false);
                    }}
                  >
                    {crop}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Season Dropdown */}
          <div className="budget-custom-dropdown" ref={seasonDropdownRef}>
            <div 
              className="budget-selected"
              onClick={() => setSeasonDropdownOpen(!seasonDropdownOpen)}
            >
              {selectedSeason || "All Seasons"}
            </div>

            {seasonDropdownOpen && (
              <div className="budget-options">
                <div
                  className="budget-option placeholder"
                  onClick={() => {
                    setSelectedSeason("");
                    setSeasonDropdownOpen(false);
                  }}
                >
                  All Seasons
                </div>

                {[...new Set(budgetData.seasonal.map((b) => b.season))].map((season) => (
                  <div
                    key={season}
                    className="budget-option"
                    onClick={() => {
                      setSelectedSeason(season);
                      setSeasonDropdownOpen(false);
                    }}
                  >
                    {season}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Season Year Dropdown */}
          <div className="budget-custom-dropdown" ref={seasonYearDropdownRef}>
            <div 
              className="budget-selected"
              onClick={() => setSeasonYearDropdownOpen(!seasonYearDropdownOpen)}
            >
              {selectedSeasonYear || "All Years"}
            </div>

            {seasonYearDropdownOpen && (
              <div className="budget-options">
                <div
                  className="budget-option placeholder"
                  onClick={() => {
                    setSelectedSeasonYear("");
                    setSeasonYearDropdownOpen(false);
                  }}
                >
                  All Years
                </div>

                {[...new Set(budgetData.seasonal.map((b) => b.year))].map((year) => (
                  <div
                    key={year}
                    className="budget-option"
                    onClick={() => {
                      setSelectedSeasonYear(year);
                      setSeasonYearDropdownOpen(false);
                    }}
                  >
                    {year}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TOTAL CARDS */}
      <h2 className="section-title">{filter.toUpperCase()} TOTALS</h2>

      <div className="stats-grid">
        {currentList.map((item) => (
          <div className="stat-card" key={item.id}>
            <div className="stat-content">
              <div className="stat-value">Rs {item.total_amount}</div>
              <div className="stat-label">
                {filter === "monthly"
                  ? `${getMonthName(item.month)} ${item.year}`
                  : filter === "seasonal"
                  ? `${item.crop_name} - ${item.season} ${item.year}`
                  : `Combined ${item.id}`}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ITEMS */}
      {(filter === "monthly" || filter === "seasonal") &&
        currentList.map((budget) => (
          <div key={budget.id} style={{ marginBottom: "20px" }}>
            <h3 className="section-title">
              {filter === "monthly"
                ? `Items - ${getMonthName(budget.month)} ${budget.year}`
                : `Items - ${budget.crop_name} (${budget.season} ${budget.year})`}
            </h3>

            <div className="stats-grid">
              {budget.items.map((item) => (
                <div className="stat-card" key={item.id}>
                  <div className="stat-content">
                    <div className="stat-value">Rs {item.price}</div>
                    <div className="stat-label">{item.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

      {/* BAR CHART */}
      <h2 className="section-title">{filter.toUpperCase()} - Bar Chart</h2>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value">
              {chartData.map((entry, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* PIE CHART */}
      <h2 className="section-title">{filter.toUpperCase()} - Pie Chart</h2>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={chartData} dataKey="value" nameKey="name" outerRadius={100} label>
              {chartData.map((entry, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* BUDGET SUMMARY TABLE */}
      <h2 className="section-title">{filter.toUpperCase()} - Budget Summary Table</h2>
      <div className="chart-container">
        <div className="finance-table-wrapper">
          <table className="finance-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Total Amount (Rs)</th>
                <th>Items Count</th>
                <th>Average Item Price</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {currentList.map((budget, index) => {
                const itemCount = budget.items ? budget.items.length : 0;
                const avgPrice = itemCount > 0 ? (Number(budget.total_amount) / itemCount).toFixed(2) : 0;
                
                return (
                  <tr key={budget.id}>
                    <td className="category-cell">
                      <span className="budget-period-badge" style={{
                        background: COLORS[index % COLORS.length] + '20',
                        color: COLORS[index % COLORS.length],
                        padding: '6px 12px',
                        borderRadius: '8px',
                        fontWeight: '600'
                      }}>
                        {filter === "monthly"
                          ? `${getMonthName(budget.month)} ${budget.year}`
                          : filter === "seasonal"
                          ? `${budget.crop_name} - ${budget.season}`
                          : `Combined #${budget.id}`}
                      </span>
                    </td>
                    <td className="amount-cell positive">
                      Rs {Number(budget.total_amount).toLocaleString()}
                    </td>
                    <td>{itemCount} items</td>
                    <td>Rs {Number(avgPrice).toLocaleString()}</td>
                    <td>
                      {filter === "seasonal" && budget.year && (
                        <span style={{ fontSize: '13px', color: '#3c6643' }}>
                          Year: {budget.year}
                        </span>
                      )}
                      {filter === "combined" && (
                        <span style={{ fontSize: '13px', color: '#3c6643' }}>
                          Mixed Budget
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {currentList.length > 0 && (
                <tr className="total-row">
                  <td><strong>TOTAL</strong></td>
                  <td className="amount-cell positive">
                    <strong>Rs {currentList.reduce((sum, b) => sum + Number(b.total_amount), 0).toLocaleString()}</strong>
                  </td>
                  <td>
                    <strong>
                      {currentList.reduce((sum, b) => sum + (b.items ? b.items.length : 0), 0)} items
                    </strong>
                  </td>
                  <td colSpan="2">
                    <strong>
                      Avg: Rs {(currentList.reduce((sum, b) => sum + Number(b.total_amount), 0) / 
                        currentList.reduce((sum, b) => sum + (b.items ? b.items.length : 0), 1)).toFixed(2)}
                    </strong>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* DETAILED ITEMS TABLE */}
      {(filter === "monthly" || filter === "seasonal") && currentList.length > 0 && (
        <>
          <h2 className="section-title">{filter.toUpperCase()} - Detailed Items Breakdown</h2>
          {currentList.map((budget, budgetIndex) => (
            <div key={budget.id} style={{ marginBottom: "30px" }}>
              <h3 style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#0f5323', 
                marginBottom: '12px',
                paddingLeft: '5px'
              }}>
                {filter === "monthly"
                  ? `${getMonthName(budget.month)} ${budget.year} - Budget Items`
                  : `${budget.crop_name} (${budget.season} ${budget.year}) - Budget Items`}
              </h3>
              
              <div className="chart-container">
                <div className="finance-table-wrapper">
                  <table className="finance-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item Name</th>
                        <th>Price (Rs)</th>
                        <th>% of Total</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {budget.items && budget.items.map((item, itemIndex) => {
                        const percentage = ((Number(item.price) / Number(budget.total_amount)) * 100).toFixed(1);
                        
                        return (
                          <tr key={item.id}>
                            <td>{itemIndex + 1}</td>
                            <td className="category-cell">
                              <span style={{ fontWeight: '500' }}>{item.name}</span>
                            </td>
                            <td className="amount-cell positive">
                              Rs {Number(item.price).toLocaleString()}
                            </td>
                            <td>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px' 
                              }}>
                                <div style={{
                                  flex: 1,
                                  height: '8px',
                                  background: 'rgba(15, 83, 35, 0.1)',
                                  borderRadius: '4px',
                                  overflow: 'hidden',
                                  maxWidth: '100px'
                                }}>
                                  <div style={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    background: COLORS[budgetIndex % COLORS.length],
                                    borderRadius: '4px'
                                  }} />
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: '600' }}>
                                  {percentage}%
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className={`status-badge ${Number(item.price) > (Number(budget.total_amount) / budget.items.length) ? 'expense' : 'income'}`}>
                                {Number(item.price) > (Number(budget.total_amount) / budget.items.length) ? 'Above Avg' : 'Below Avg'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {budget.items && budget.items.length > 0 && (
                        <tr className="total-row">
                          <td colSpan="2"><strong>SUBTOTAL</strong></td>
                          <td className="amount-cell positive">
                            <strong>Rs {Number(budget.total_amount).toLocaleString()}</strong>
                          </td>
                          <td><strong>100%</strong></td>
                          <td><strong>{budget.items.length} items</strong></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {/* COMPARATIVE ANALYSIS TABLE */}
      {currentList.length > 1 && (
        <>
          <h2 className="section-title">{filter.toUpperCase()} - Comparative Analysis</h2>
          <div className="chart-container">
            <div className="finance-table-wrapper">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Total Budget</th>
                    <th>vs Average</th>
                    <th>vs Highest</th>
                    <th>Ranking</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const avgBudget = currentList.reduce((sum, b) => sum + Number(b.total_amount), 0) / currentList.length;
                    const maxBudget = Math.max(...currentList.map(b => Number(b.total_amount)));
                    const sorted = [...currentList].sort((a, b) => Number(b.total_amount) - Number(a.total_amount));

                    return currentList.map((budget) => {
                      const vsAvg = ((Number(budget.total_amount) - avgBudget) / avgBudget * 100).toFixed(1);
                      const vsMax = ((Number(budget.total_amount) / maxBudget) * 100).toFixed(1);
                      const rank = sorted.findIndex(b => b.id === budget.id) + 1;

                      return (
                        <tr key={budget.id}>
                          <td>
                            {filter === "monthly"
                              ? `${getMonthName(budget.month)} ${budget.year}`
                              : filter === "seasonal"
                              ? `${budget.crop_name}`
                              : `Combined #${budget.id}`}
                          </td>
                          <td className="amount-cell positive">
                            Rs {Number(budget.total_amount).toLocaleString()}
                          </td>
                          <td className={Number(vsAvg) >= 0 ? 'positive' : 'negative'}>
                            {Number(vsAvg) >= 0 ? '+' : ''}{vsAvg}%
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                flex: 1,
                                height: '8px',
                                background: 'rgba(15, 83, 35, 0.1)',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                maxWidth: '120px'
                              }}>
                                <div style={{
                                  width: `${vsMax}%`,
                                  height: '100%',
                                  background: rank === 1 ? '#00a859' : '#387eff',
                                  borderRadius: '4px'
                                }} />
                              </div>
                              <span style={{ fontSize: '13px', fontWeight: '600' }}>
                                {vsMax}%
                              </span>
                            </div>
                          </td>
                          <td>
                            <span style={{
                              padding: '5px 10px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: rank === 1 ? 'rgba(0, 168, 89, 0.2)' : 
                                         rank === sorted.length ? 'rgba(255, 78, 78, 0.2)' : 
                                         'rgba(56, 126, 255, 0.2)',
                              color: rank === 1 ? '#006636' : 
                                    rank === sorted.length ? '#a80000' : 
                                    '#387eff'
                            }}>
                              #{rank} of {sorted.length}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Budget;